import React, { useState, useMemo, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Book, Printer, CreditCard, Eye, EyeOff, MapPin, Loader2, Check, AlertCircle } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import BookPreview from './BookPreview';
import DateRangeSelector from './DateRangeSelector';
import ContentFilter from './ContentFilter';
import AddressForm from './AddressForm';
import BookEditor from './BookEditor';
import { trackPrintOrderStarted } from '../../analytics';

// Initialize Stripe - replace with your publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here');

// Payment form component
const CheckoutForm = ({ onSuccess, onError, processing, setProcessing }) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required'
    });

    if (error) {
      onError(error.message);
      setProcessing(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full mt-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5" />
            Pay & Place Order
          </>
        )}
      </button>
    </form>
  );
};

const PrintBookFlow = ({ 
  isOpen, 
  onClose, 
  babyBook, 
  entries, 
  milestones,
  userProfile 
}) => {
  const [step, setStep] = useState(1);
  const [dateRange, setDateRange] = useState(null);
  const [contentFilter, setContentFilter] = useState({
    photos: true,
    journalEntries: true,
    milestones: true,
    preBirth: true,
    birthDetails: true
  });
  const [format, setFormat] = useState('hardcover');
  const [showPreview, setShowPreview] = useState(false);
  
  // New state for order flow
  const [shippingAddress, setShippingAddress] = useState({
    name: userProfile?.displayName || '',
    email: userProfile?.email || '',
    countryCode: 'US'
  });
  const [addressErrors, setAddressErrors] = useState({});
  
  // Processing states
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  
  // Order data
  const [pdfData, setPdfData] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);
  const [draftId, setDraftId] = useState(null);
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false);

  const functions = getFunctions();

  // Product mapping
  const productMapping = {
    'hardcover': 'premium-square',
    'softcover': 'classic-square'
  };

  // Product ID mapping for draft generation
  const draftProductMapping = {
    'hardcover': 'premium-hardcover',
    'softcover': 'classic-softcover'
  };

  const pricing = {
    hardcover: {
      base: 29.99,
      label: 'Hardcover',
      description: 'Premium hardcover with matte finish'
    },
    softcover: {
      base: 19.99,
      label: 'Softcover',
      description: 'Lightweight and flexible'
    }
  };

  const totalSteps = 6;
  const stepLabels = ['Period', 'Content', 'Format', 'Edit Book', 'Shipping', 'Payment'];

  // Get filtered entries count
  const filteredEntries = useMemo(() => {
    if (!entries) return [];
    
    return entries.filter(entry => {
      const entryDate = entry.timestamp?.toDate ? entry.timestamp.toDate() : new Date(entry.timestamp);
      if (dateRange?.start && entryDate < new Date(dateRange.start)) return false;
      if (dateRange?.end && entryDate > new Date(dateRange.end)) return false;
      return true;
    });
  }, [entries, dateRange]);

  const filteredPhotos = useMemo(() => {
    if (!contentFilter.photos) return [];
    return filteredEntries.flatMap(entry => entry.photos || entry.imageUrls || []);
  }, [filteredEntries, contentFilter.photos]);

  // Validate shipping address
  const validateAddress = () => {
    const errors = {};
    if (!shippingAddress.name?.trim()) errors.name = 'Name is required';
    if (!shippingAddress.line1?.trim()) errors.line1 = 'Address is required';
    if (!shippingAddress.city?.trim()) errors.city = 'City is required';
    if (!shippingAddress.postalCode?.trim()) errors.postalCode = 'Postal code is required';
    if (shippingAddress.countryCode === 'US' && !shippingAddress.state) {
      errors.state = 'State is required';
    }
    setAddressErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return dateRange?.start && dateRange?.end;
      case 2:
        return Object.values(contentFilter).some(Boolean);
      case 3:
        return format;
      case 4:
        return draftId; // Need draft to proceed from editor
      case 5:
        return shippingAddress.name && shippingAddress.line1 && shippingAddress.city && shippingAddress.postalCode;
      case 6:
        return clientSecret;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    setError(null);
    
    // Generate draft when leaving step 3 (format selection)
    if (step === 3 && canProceed() && !draftId) {
      setIsGeneratingDraft(true);
      try {
        const generateBookDraft = httpsCallable(functions, 'generateBookDraft');
        const draftResult = await generateBookDraft({
          babyBookId: babyBook.id,
        draftId,
          productId: draftProductMapping[format],
          contentFilter: {
            includePhotos: contentFilter.photos,
            includeEntries: contentFilter.journalEntries,
            includeMilestones: contentFilter.milestones
          },
          dateRange
        });
        console.log('Draft created:', draftResult.data);
        setDraftId(draftResult.data.draftId);
        setIsGeneratingDraft(false);
        setStep(4);
      } catch (err) {
        console.error('generateBookDraft error:', err);
        setError('Failed to generate book draft: ' + err.message);
        setIsGeneratingDraft(false);
      }
      return;
    }
    
    if (step === 5) {
      // Validate address before proceeding
      if (!validateAddress()) return;
      
      // Generate PDF and get quote
      await generatePdfAndQuote();
    } else if (step < totalSteps && canProceed()) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setError(null);
    if (step > 1) {
      setStep(step - 1);
      // Clear payment state if going back from payment
      if (step === 6) {
        setClientSecret(null);
        setPaymentIntentId(null);
      }
      // Clear draft if going back from editor
      if (step === 4) {
        setDraftId(null);
      }
    }
  };

  // Generate PDF and get Prodigi quote
  const generatePdfAndQuote = async () => {
    setIsGeneratingPdf(true);
    setError(null);
    
    try {
      const productId = productMapping[format];
      
      // Step 1: Generate PDF
      const generatePdf = httpsCallable(functions, 'generateBookPdf');
      const pdfResult = await generatePdf({
        babyBookId: babyBook.id,
        draftId,
        dateRange,
        contentFilter,
        productId
      });
      
      if (!pdfResult.data.success) {
        throw new Error('Failed to generate PDF');
      }
      
      setPdfData(pdfResult.data);
      setIsGeneratingPdf(false);
      setIsLoadingQuote(true);
      
      // Step 2: Get quote from Prodigi
      const getQuote = httpsCallable(functions, 'getProdigiQuote');
      const quoteResult = await getQuote({
        productId,
        pageCount: pdfResult.data.pageCount,
        countryCode: shippingAddress.countryCode,
        shippingMethod: 'standard'
      });
      
      if (!quoteResult.data.success) {
        throw new Error('Failed to get quote');
      }
      
      setQuoteData(quoteResult.data);
      setIsLoadingQuote(false);
      
      // Step 3: Create Stripe payment intent
      trackPrintOrderStarted(babyBook.id, pdfResult.data.pageCount);
      const totalCents = Math.round(parseFloat(quoteResult.data.pricing.totalPrice) * 100);
      
      const createPayment = httpsCallable(functions, 'createPrintOrderPaymentIntent');
      const paymentResult = await createPayment({
        amount: totalCents,
        currency: 'usd',
        babyBookId: babyBook.id,
        draftId,
        productId,
        pageCount: pdfResult.data.pageCount
      });
      
      if (!paymentResult.data.success) {
        throw new Error('Failed to create payment');
      }
      
      setClientSecret(paymentResult.data.clientSecret);
      setPaymentIntentId(paymentResult.data.paymentIntentId);
      setStep(6);
      
    } catch (err) {
      console.error('Order preparation error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setIsGeneratingPdf(false);
      setIsLoadingQuote(false);
    }
  };

  // Handle successful payment
  const handlePaymentSuccess = async (confirmedPaymentIntentId) => {
    setIsSubmittingOrder(true);
    setError(null);
    
    try {
      const productId = productMapping[format];
      
      // Submit order to Prodigi
      const submitOrder = httpsCallable(functions, 'submitProdigiOrder');
      const orderResult = await submitOrder({
        productId,
        pdfUrl: pdfData.pdfUrl,
        pageCount: pdfData.pageCount,
        shippingAddress,
        babyBookId: babyBook.id,
        draftId,
        stripePaymentIntentId: confirmedPaymentIntentId,
        recipientCost: quoteData.pricing.totalPrice,
        shippingMethod: 'standard'
      });
      
      if (!orderResult.data.success) {
        throw new Error('Failed to submit order');
      }
      
      setOrderDetails(orderResult.data);
      setOrderComplete(true);
      
    } catch (err) {
      console.error('Order submission error:', err);
      setError(err.message || 'Payment succeeded but order submission failed. Please contact support.');
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const handlePaymentError = (message) => {
    setError(message);
    setIsProcessingPayment(false);
  };

  if (!isOpen) return null;

  // Order complete screen
  if (orderComplete) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl w-full max-w-md p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Order Placed!</h2>
          <p className="text-gray-600 mb-4">
            Your book is being printed and will ship within 3-5 business days.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm text-left">
            <p><strong>Order ID:</strong> {orderDetails?.orderId}</p>
            <p><strong>Pages:</strong> {pdfData?.pageCount}</p>
            <p><strong>Format:</strong> {pricing[format].label}</p>
            <p><strong>Total:</strong> ${quoteData?.pricing?.totalPrice}</p>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            You'll receive an email with tracking information once your book ships.
          </p>
          <button
            onClick={onClose}
            className="w-full py-3 bg-ocean-500 text-white rounded-lg font-semibold hover:bg-ocean-600"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className={`bg-white rounded-xl w-full overflow-hidden flex flex-col ${step === 4 ? "max-w-[98vw] max-h-[98vh] sm:max-w-[95vw] sm:max-h-[95vh]" : "max-w-4xl max-h-[95vh] sm:max-h-[90vh]"}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b bg-gradient-to-r from-ocean-50 to-peach-50">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-ocean-100 rounded-full flex items-center justify-center">
              <Book className="w-4 h-4 sm:w-5 sm:h-5 text-ocean-600" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-ocean-900">Print Your Book</h2>
              <p className="text-xs sm:text-sm text-ocean-700 truncate max-w-[150px] sm:max-w-none">{babyBook?.babyName}'s Memory Book</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-ocean-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 border-b">
          <div className="flex items-center justify-between">
            {stepLabels.map((label, idx) => (
              <div
                key={idx}
                className={`flex items-center ${idx < stepLabels.length - 1 ? 'flex-1' : ''}`}
              >
                <div className="flex flex-col items-center">
                  <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold ${
                    step > idx + 1 
                      ? 'bg-ocean-500 text-white' 
                      : step === idx + 1 
                        ? 'bg-ocean-100 text-ocean-900 ring-2 ring-ocean-500' 
                        : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > idx + 1 ? '✓' : idx + 1}
                  </div>
                  <span className={`text-xs mt-1 hidden sm:block ${
                    step === idx + 1 ? 'text-ocean-900 font-medium' : 'text-gray-500'
                  }`}>{label}</span>
                </div>
                {idx < stepLabels.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 sm:mx-2 ${
                    step > idx + 1 ? 'bg-ocean-500' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-ocean-800 mt-2 sm:hidden font-medium">
            Step {step}: {stepLabels[step - 1]}
          </p>
        </div>

        {/* Error display */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {/* Mobile Preview Toggle - hide on step 4 (editor) and step 6 (payment) */}
          {step < 6 && step !== 4 && (
            <div className="mb-4 sm:hidden">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-ocean-100 text-ocean-800 rounded-lg text-sm font-medium"
              >
                {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showPreview ? 'Hide Preview' : 'Show Book Preview'}
              </button>
            </div>
          )}

          {/* Mobile Preview (collapsible) */}
          {showPreview && step < 6 && step !== 4 && (
            <div className="mb-4 sm:hidden">
              <div className="bg-gray-50 rounded-xl p-3 flex justify-center">
                <div className="transform scale-75 origin-top">
                  <BookPreview
                    babyBook={babyBook}
                    entries={entries}
                    milestones={milestones}
                    dateRange={dateRange}
                    contentFilter={contentFilter}
                    format={format}
                  />
                </div>
              </div>
            </div>
          )}

          <div className={`grid ${step < 6 && step !== 4 ? 'md:grid-cols-2' : ''} gap-4 sm:gap-6`}>
            {/* Left side - Controls */}
            <div>
              {step === 1 && (
                <DateRangeSelector
                  babyBook={babyBook}
                  entries={entries}
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                />
              )}

              {step === 2 && (
                <ContentFilter
                  contentFilter={contentFilter}
                  onContentFilterChange={setContentFilter}
                  entries={entries}
                  milestones={milestones}
                  babyBook={babyBook}
                />
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <h3 className="text-base sm:text-lg font-semibold text-ocean-900 flex items-center gap-2">
                    <Printer className="w-5 h-5" />
                    Choose Format
                  </h3>

                  <div className="space-y-2 sm:space-y-3">
                    {Object.entries(pricing).map(([key, value]) => (
                      <button
                        key={key}
                        onClick={() => setFormat(key)}
                        className={`w-full p-3 sm:p-4 rounded-lg border-2 text-left transition-all ${
                          format === key
                            ? 'border-ocean-500 bg-ocean-50'
                            : 'border-gray-200 hover:border-ocean-300 bg-white'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className={`font-semibold text-sm sm:text-base ${format === key ? 'text-ocean-900' : 'text-gray-900'}`}>
                              {value.label}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1">{value.description}</p>
                          </div>
                          <p className={`text-lg sm:text-xl font-bold ${format === key ? 'text-ocean-600' : 'text-gray-700'}`}>
                            ${value.base.toFixed(2)}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>

                  <div className="bg-ocean-50 border border-ocean-200 rounded-lg p-3 text-xs sm:text-sm">
                    <p className="text-ocean-800">
                      <strong>Includes:</strong> Professional printing, worldwide shipping, 
                      premium {format === 'hardcover' ? '170gsm silk' : 'matte'} paper
                    </p>
                  </div>
                  
                  {isGeneratingDraft && (
                    <div className="flex items-center justify-center gap-2 py-4 text-ocean-700">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Creating your book layout...</span>
                    </div>
                  )}
                </div>
              )}

              {step === 4 && draftId && (
                <div className="h-[80vh] -mx-3 sm:-mx-6 col-span-full">
                  <BookEditor
                    draftId={draftId}
                    babyBookId={babyBook.id}
                    onComplete={() => setStep(5)}
                    onBack={() => {
                      setDraftId(null);
                      setStep(3);
                    }}
                  />
                </div>
              )}

              {step === 5 && (
                <div className="space-y-4">
                  <AddressForm
                    address={shippingAddress}
                    onChange={setShippingAddress}
                    errors={addressErrors}
                  />
                  
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-2 text-sm">
                    <h4 className="font-semibold text-gray-900">Order Summary</h4>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Format</span>
                      <span className="font-medium">{pricing[format].label}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Entries</span>
                      <span className="font-medium">{filteredEntries.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Photos</span>
                      <span className="font-medium">{filteredPhotos.length}</span>
                    </div>
                    <div className="border-t pt-2 mt-2">
                      <p className="text-xs text-gray-500">
                        Final price including shipping will be calculated next.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {step === 6 && (
                <div className="space-y-4">
                  <h3 className="text-base sm:text-lg font-semibold text-ocean-900 flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Payment
                  </h3>

                  {/* Order summary */}
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Book ({pdfData?.pageCount} pages)</span>
                      <span className="font-medium">{pricing[format].label}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Shipping to {shippingAddress.countryCode}</span>
                      <span className="font-medium">Included</span>
                    </div>
                    <div className="border-t pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-900">Total</span>
                        <span className="font-bold text-lg text-ocean-600">
                          ${quoteData?.pricing?.totalPrice || '—'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Shipping address summary */}
                  <div className="bg-ocean-50 border border-ocean-200 rounded-lg p-3 text-sm">
                    <p className="font-medium text-ocean-900 mb-1">Shipping to:</p>
                    <p className="text-ocean-800">
                      {shippingAddress.name}<br />
                      {shippingAddress.line1}<br />
                      {shippingAddress.line2 && <>{shippingAddress.line2}<br /></>}
                      {shippingAddress.city}, {shippingAddress.state} {shippingAddress.postalCode}<br />
                      {shippingAddress.countryCode}
                    </p>
                  </div>

                  {/* Stripe payment form */}
                  {clientSecret && (
                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                      <CheckoutForm
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                        processing={isProcessingPayment || isSubmittingOrder}
                        setProcessing={setIsProcessingPayment}
                      />
                    </Elements>
                  )}

                  {isSubmittingOrder && (
                    <div className="flex items-center justify-center gap-2 py-4 text-ocean-700">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Submitting your order...</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right side - Preview (desktop only) */}
            {step < 6 && (
              <div className="hidden md:flex flex-col items-center justify-center bg-gray-50 rounded-xl p-4 min-h-[400px]">
                <BookPreview
                  babyBook={babyBook}
                  entries={entries}
                  milestones={milestones}
                  dateRange={dateRange}
                  contentFilter={contentFilter}
                  format={format}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-t bg-gray-50">
          <button
            onClick={step === 1 ? onClose : handleBack}
            disabled={isGeneratingPdf || isLoadingQuote || isProcessingPayment || isSubmittingOrder}
            className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors text-sm sm:text-base disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          {step < 6 && (
            <button
              onClick={handleNext}
              disabled={!canProceed() || isGeneratingPdf || isLoadingQuote}
              className="flex items-center gap-1 sm:gap-2 px-4 sm:px-6 py-2 bg-ocean-500 text-white rounded-lg font-semibold hover:bg-ocean-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
            >
              {isGeneratingPdf ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Generating PDF...</span>
                </>
              ) : isLoadingQuote ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Getting quote...</span>
                </>
              ) : (
                <>
                  {step === 4 ? 'Continue to Payment' : 'Continue'}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrintBookFlow;
